import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@wondermaps/ui";

interface SearchData {
  topSearches: { query: string; count: number }[];
  zeroResults: { query: string; count: number }[];
  totalSearches: number;
  convertedSearches: number;
  conversionRate: number;
}

export function SearchIntent({ data }: { data: SearchData }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Search Intent</CardTitle>
        <CardDescription>
          Reveals what visitors are looking for. Top searches confirm popular
          content; zero-result queries expose gaps in POI coverage.
          Conversion rate shows how often a search leads to a POI tap.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Summary stats */}
        <div className="grid grid-cols-3 gap-4">
          <StatBox label="Total Searches" value={data.totalSearches.toLocaleString()} />
          <StatBox label="Converted" value={data.convertedSearches.toLocaleString()} />
          <StatBox label="Conversion Rate" value={`${data.conversionRate}%`} />
        </div>

        {/* Top searches */}
        <div>
          <h4 className="text-sm font-medium mb-2">Top Searches</h4>
          {data.topSearches.length === 0 ? (
            <p className="text-muted-foreground text-sm">No search data.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Query</TableHead>
                  <TableHead className="text-right">Count</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.topSearches.map((s) => (
                  <TableRow key={s.query}>
                    <TableCell>{s.query}</TableCell>
                    <TableCell className="text-right">{s.count.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Zero results */}
        {data.zeroResults.length > 0 && (
          <div>
            <h4 className="text-sm font-medium mb-2 text-destructive">Zero-Result Searches</h4>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Query</TableHead>
                  <TableHead className="text-right">Count</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.zeroResults.map((s) => (
                  <TableRow key={s.query}>
                    <TableCell>{s.query}</TableCell>
                    <TableCell className="text-right">{s.count.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border p-3 text-center">
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
